/**
 * Block record bridge helpers — splitting of dxfBridge.ts because the
 * BLOCK_RECORD <-> drawing-model conversion logic is a self-contained
 * unit that can be tested in isolation. Keeping it in its own module
 * prevents dxfBridge.ts from growing past the 2000-line guardrail
 * (CLAUDE.md: ">2000 не допускать; разбивать в рамках той же задачи").
 *
 * Public surface:
 *   - buildBlockRecord(blockDef, entry?): BlockRecord
 *   - importBlockRecords(blocksSection, tablesSection): Record<name, BlockRecord>
 *   - exportBlockRecords(blockRecords): DxfBlockRecordEntry[]
 *
 * dxfBridge.ts re-exports buildBlockRecord via a barrel so external
 * callers still import it from `./dxfBridge`.
 */
import type {
  DxfBlockDefinition,
  DxfBlockRecordEntry,
  DxfBlocksSection,
  DxfTablesSection,
} from "@dxf-ai-editor/dxf-io";

import type { BlockRecord } from "./model";

/**
 * Merge a BLOCK header (from the BLOCKS section) with the matching
 * BLOCK_RECORD entry (from the TABLES section) into a single, drawing-
 * model-ready BlockRecord. The BLOCKS section supplies the description
 * (group 4); BLOCK_RECORD supplies insertion units, explodability,
 * scalability, layout handle and annotative flag. Missing BLOCK_RECORD
 * entry produces a BlockRecord with AutoCAD default values.
 */
export function buildBlockRecord(
  blockDef: DxfBlockDefinition,
  recordEntry: DxfBlockRecordEntry | undefined,
): BlockRecord {
  const scalability = recordEntry?.scalability ?? 0;
  const entry: BlockRecord = {
    name: blockDef.name,
    insertionUnits: recordEntry?.insertionUnits ?? 0,
    explodable: recordEntry?.explodability !== 0, // default 1 / undefined → true
    scalable: scalability === 1 ? "uniform" : "non-uniform",
    isAnnotative: recordEntry?.isAnnotative ?? false,
    annotativeDecompositions: {},
    description: blockDef.description ?? "",
  };
  if (recordEntry?.layoutHandle) entry.layoutHandle = recordEntry.layoutHandle;
  // DYN-4 Phase 6 — capture the BLOCK_RECORD handle + extension-dict
  // pointer so round-trip preserves the eval_graph chain.
  if (recordEntry?.handle) entry.handle = recordEntry.handle;
  // T9b — carry the record's `330` owner (the BLOCK_RECORD table head).
  if (recordEntry?.ownerHandle) entry.ownerHandle = recordEntry.ownerHandle;
  if (recordEntry?.extensionDictionaryHandle)
    entry.extensionDictionaryHandle = recordEntry.extensionDictionaryHandle;
  // Batch-6 S-BLOCKRECORD-REACTORS — carry the persistent-reactor handles
  // (102/{ACAD_REACTORS}+330) through the app import path so export can
  // re-emit them. Omit when absent/empty. (Codex#248 F1 P2.)
  if (recordEntry?.reactorHandles && recordEntry.reactorHandles.length > 0)
    entry.reactorHandles = recordEntry.reactorHandles;
  return entry;
}

export function importBlockRecords(
  blocksSection: DxfBlocksSection | null,
  tablesSection: DxfTablesSection | null,
): Record<string, BlockRecord> {
  const out: Record<string, BlockRecord> = {};
  if (!blocksSection) return out;
  // Build a (name → BLOCK_RECORD entry) lookup so the buildBlockRecord
  // helper can splice in TABLES-section metadata by block name.
  const tableByName = new Map<string, DxfBlockRecordEntry>();
  if (tablesSection) {
    for (const rec of tablesSection.blockRecords) {
      if (rec.name) tableByName.set(rec.name, rec);
    }
  }
  for (const block of blocksSection.blocks) {
    if (!block.name) continue;
    out[block.name] = buildBlockRecord(block, tableByName.get(block.name));
  }
  // AutoCAD always has Model_Space and Paper_Space entries in the
  // BLOCK_RECORD table even when the BLOCKS section does not list the
  // matching (empty) BLOCK. Seed them here so the drawing-model matches
  // AutoCAD conventions.
  for (const required of ["*Model_Space", "*Paper_Space"]) {
    if (!out[required]) {
      const rec = tableByName.get(required);
      out[required] = {
        name: required,
        insertionUnits: rec?.insertionUnits ?? 0,
        explodable: rec?.explodability !== 0,
        scalable: rec?.scalability === 1 ? "uniform" : "non-uniform",
        isAnnotative: rec?.isAnnotative ?? false,
        annotativeDecompositions: {},
        description: "",
        ...(rec?.layoutHandle ? { layoutHandle: rec.layoutHandle } : {}),
        ...(rec?.handle ? { handle: rec.handle } : {}),
        ...(rec?.ownerHandle ? { ownerHandle: rec.ownerHandle } : {}), // T9b
        ...(rec?.extensionDictionaryHandle
          ? { extensionDictionaryHandle: rec.extensionDictionaryHandle }
          : {}),
        ...(rec?.reactorHandles && rec.reactorHandles.length > 0
          ? { reactorHandles: rec.reactorHandles }
          : {}),
      };
    }
  }
  return out;
}

export function exportBlockRecords(
  blockRecords: Record<string, BlockRecord> | undefined,
): DxfBlockRecordEntry[] {
  if (!blockRecords) return [];
  const out: DxfBlockRecordEntry[] = [];
  for (const [name, record] of Object.entries(blockRecords)) {
    const entry: DxfBlockRecordEntry = {
      name,
      insertionUnits: record.insertionUnits,
      explodability: record.explodable ? 1 : 0,
      scalability: record.scalable === "uniform" ? 1 : 0,
    };
    if (record.layoutHandle) entry.layoutHandle = record.layoutHandle;
    if (record.isAnnotative) entry.isAnnotative = true;
    // DYN-4 Phase 6 — round-trip BLOCK_RECORD handle + extension-dict.
    if (record.handle) entry.handle = record.handle;
    // T9b — re-emit the `330` owner (BLOCK_RECORD table head).
    if (record.ownerHandle) entry.ownerHandle = record.ownerHandle;
    if (record.extensionDictionaryHandle)
      entry.extensionDictionaryHandle = record.extensionDictionaryHandle;
    // Batch-6 S-BLOCKRECORD-REACTORS — re-emit the persistent-reactor block
    // so the dxf-io writer round-trips it. (Codex#248 F1 P2.)
    if (record.reactorHandles && record.reactorHandles.length > 0)
      entry.reactorHandles = record.reactorHandles;
    out.push(entry);
  }
  return out;
}
